import { Scene, UniversalCamera, Vector3 } from "../../app/babylon";

const FOLLOW_DISTANCE = 9.0;
const FOLLOW_HEIGHT = 4.8;
const LOOK_HEIGHT = 1.4;
const POS_LERP = 4;
const LOOK_LERP = 6;
const SWING_GAIN = 1.2;

export class ChaseCamera {
  readonly camera: UniversalCamera;

  private smoothedPos = new Vector3(0, 5, -5);
  private smoothedLook = new Vector3(0, 1, 0);
  private headingPrev = 0;
  private headingRate = 0;

  constructor(scene: Scene) {
    this.camera = new UniversalCamera("chase", new Vector3(0, 5, -5), scene);
    this.camera.fov = 1.15;
    this.camera.minZ = 0.05;
    this.camera.maxZ = 1500;
  }

  warpTo(pos: Vector3, heading: number): void {
    const fx = Math.sin(heading);
    const fz = Math.cos(heading);
    this.smoothedPos.set(
      pos.x - fx * FOLLOW_DISTANCE,
      pos.y + FOLLOW_HEIGHT,
      pos.z - fz * FOLLOW_DISTANCE
    );
    this.smoothedLook.set(pos.x, pos.y + LOOK_HEIGHT, pos.z);
    this.headingPrev = heading;
    this.camera.position.copyFrom(this.smoothedPos);
    this.camera.setTarget(this.smoothedLook.clone());
  }

  update(dt: number, riderPos: Vector3, heading: number): void {
    const dh = wrapAngle(heading - this.headingPrev);
    this.headingPrev = heading;
    const rate = dt > 0 ? dh / dt : 0;
    this.headingRate += (rate - this.headingRate) * Math.min(1, 6 * dt);

    const fx = Math.sin(heading);
    const fz = Math.cos(heading);
    const rx = fz;
    const rz = -fx;

    const lateralSwing = -this.headingRate * SWING_GAIN;

    const targetPos = new Vector3(
      riderPos.x - fx * FOLLOW_DISTANCE + rx * lateralSwing,
      riderPos.y + FOLLOW_HEIGHT,
      riderPos.z - fz * FOLLOW_DISTANCE + rz * lateralSwing
    );

    const targetLook = new Vector3(
      riderPos.x + fx * 1.5,
      riderPos.y + LOOK_HEIGHT,
      riderPos.z + fz * 1.5
    );

    const kPos = 1 - Math.exp(-POS_LERP * dt);
    const kLook = 1 - Math.exp(-LOOK_LERP * dt);

    this.smoothedPos.x += (targetPos.x - this.smoothedPos.x) * kPos;
    this.smoothedPos.y += (targetPos.y - this.smoothedPos.y) * kPos;
    this.smoothedPos.z += (targetPos.z - this.smoothedPos.z) * kPos;

    this.smoothedLook.x += (targetLook.x - this.smoothedLook.x) * kLook;
    this.smoothedLook.y += (targetLook.y - this.smoothedLook.y) * kLook;
    this.smoothedLook.z += (targetLook.z - this.smoothedLook.z) * kLook;

    this.camera.position.copyFrom(this.smoothedPos);
    this.camera.setTarget(this.smoothedLook.clone());
  }
}

function wrapAngle(a: number): number {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}
